import { useAnimationLoop } from "../utils/useAnimationLoop";
import { useCanvas } from "../utils/useCanvas";
import { mod } from "../utils/mathUtils";
import { useEffect, useRef } from "react";
import { isMobile } from "../utils/isMobile"; // Utility to detect mobile devices
import { useLenis } from "lenis/react";

const defaultOptions = {
  normalPlaybackSpeed: 0.5,
  minPlaybackSpeed: -99999,
  maxPlaybackSpeed: 99999,
  velocitySensitivity: 10, // pixels/second divisor
  frames: {
    desktop: {
      count: 240,
      fps: 120,
      directory: "/rotatingTorus/desktop",
      frontDirectory: "/torusFront/desktop",
    },
    mobile: {
      count: 120,
      fps: 60,
      directory: "/rotatingTorus/mobile",
      frontDirectory: "/torusFront/mobile",
    },
  },
};

export type Options = typeof defaultOptions;

export function TorusTwice({
  options = defaultOptions,
}: {
  options?: Options;
}) {
  const isMobileDevice = isMobile();
  const { directory, frontDirectory, count, fps } = isMobileDevice
    ? options.frames.mobile
    : options.frames.desktop;

  const [ctx, canvas, setCanvas] = useCanvas({
    autoResize: true,
    contextId: "2d",
  });
  const [frontCtx, frontCanvas, setFrontCanvas] = useCanvas({
    autoResize: true,
    contextId: "2d",
  });
  const timeRef = useRef(0);

  const imagesRef = useRef<HTMLImageElement[]>([]);
  const frontImagesRef = useRef<HTMLImageElement[]>([]);

  useEffect(() => {
    const loadImages = async () => {
      const imagePromises: Promise<HTMLImageElement>[] = [];
      const frontImagePromises: Promise<HTMLImageElement>[] = [];
      for (let i = 1; i <= count; i++) {
        const frameNumber = i.toString().padStart(3, "0");
        const url = `${directory}/frame_${frameNumber}.webp`;
        imagePromises.push(
          new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(img);
          }),
        );
        const frontUrl = `${frontDirectory}/frame_${frameNumber}.webp`;
        frontImagePromises.push(
          new Promise((resolve) => {
            const img = new Image();
            img.src = frontUrl;
            img.onload = () => resolve(img);
          }),
        );
      }
      const loadedImages = await Promise.all(imagePromises);
      const loadedFrontImages = await Promise.all(frontImagePromises);
      imagesRef.current = loadedImages;
      frontImagesRef.current = loadedFrontImages;
      document.body.classList.remove("loading");
    };

    loadImages();
  }, []);

  const velocityRef = useRef(0);

  useLenis((lenis) => {
    velocityRef.current = lenis.velocity;
  }, []);

  useAnimationLoop((dt, t) => {
    const images = imagesRef.current;
    const frontImages = frontImagesRef.current;
    if (!ctx || !frontCtx || !canvas || images.length === 0 || frontImages.length === 0)
      return;

    const speedMultiplier = velocityRef.current / options.velocitySensitivity;
    const speed = options.normalPlaybackSpeed + speedMultiplier;
    timeRef.current += (dt / 1000) * speed;

    const at = timeRef.current * fps;

    const frameCount = images.length;
    const frameIndex = Math.floor(mod(at, frameCount));
    const prevFrameIndex = Math.floor(mod(at - 1, frameCount));
    const blend = at - Math.floor(at);
    const image = images[frameIndex]!;
    const frontImage = frontImages[frameIndex]!;

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    frontCtx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate aspect ratio, scale, and offset for centering horizontally and positioning at the bottom
    const canvasAspect = canvas.width / canvas.height;
    const imageAspect = image.width / image.height;
    let drawWidth = canvas.width;
    let drawHeight = canvas.height;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspect > imageAspect) {
      drawWidth = canvas.height * imageAspect;
      offsetX = (canvas.width - drawWidth) / 2;
    } else {
      drawHeight = canvas.width / imageAspect;
      offsetY = (canvas.height - drawHeight) / 2; // Center vertically
    }

    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

    if (blend > 0 && frameIndex !== prevFrameIndex) {
      const prevImage = images[prevFrameIndex]!;
      ctx.globalAlpha = 1 - blend;
      ctx.drawImage(prevImage, offsetX, offsetY, drawWidth, drawHeight);
      ctx.globalAlpha = 1; // Reset alpha
    }

    frontCtx.drawImage(frontImage, offsetX, offsetY, drawWidth, drawHeight);

    if (blend > 0 && frameIndex !== prevFrameIndex) {
      const prevImage = frontImages[prevFrameIndex]!;
      frontCtx.globalAlpha = 1 - blend;
      frontCtx.drawImage(prevImage, offsetX, offsetY, drawWidth, drawHeight);
      frontCtx.globalAlpha = 1; // Reset alpha
    }
  });

  return (
    <>
      <canvas
        ref={setCanvas}
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: -3,
        }}
      />
      <canvas
        ref={setFrontCanvas}
        style={{
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: -1,
        }}
      />
    </>
  );
}
